import { type MouseEventHandler, type MutableRefObject } from 'react';
import {
    isDOMEvent,
    isDOMElement,
    type FilterKeys,
    type DOMEvent,
    type DOMElement,
} from './typescriptFunctions';

let passiveIfSupported: false | { passive: true } = false;
const noop = (): void => {};

try {
    window.addEventListener(
        'click',
        noop,
        Object.defineProperty({}, 'passive', {
            get: function (): boolean {
                passiveIfSupported = { passive: true };
                return true;
            },
        })
    );
    window.removeEventListener('click', noop);
} catch (err) {
    // do nothing
}

const _constants = {
    // limit for x/y after translating to center
    MAX_RANGE: 0.5,
    // default to limiting by dimension, not scale
    MAX_SCALE_DEFAULT: Infinity,
    // regular PDP max width
    MAX_SIZE_OLD: 1500,
    // let the image scale to it's natural size
    MAX_SIZE_IMAGE: Infinity,
    MAX_SIZE_CONTAINER_FIT: 'MAX_SIZE_CONTAINER_FIT',
    MAX_SIZE_CONTAINER_WIDTH: 'MAX_SIZE_CONTAINER_WIDTH',
    MAX_SIZE_CONTAINER_HEIGHT: 'MAX_SIZE_CONTAINER_HEIGHT',
} as const;

// set new const to _constants to include 'MAX_SIZE_WINDOW' type
const CONSTANTS = _constants;
type Constants = typeof CONSTANTS;
type Sizes = FilterKeys<Constants, `MAX_SIZE${string}`>;

const defaultConfig = {
    maxSize: 'MAX_SIZE_IMAGE',
    maxScale: CONSTANTS.MAX_SCALE_DEFAULT,
    movementScale: 1.2,
} as const;

function clamp(arg: number, min: number, max: number): number {
    return Math.min(Math.max(arg, min), max);
}

// clamp to range from origin of cartesian coordinate system
function cartClamp(arg: number, range: number): number {
    const max = Math.abs(range);
    const min = -max;
    return clamp(arg, min, max);
}

function ceilAbs(arg: number): number {
    const sign = Math.sign(arg);
    return Math.ceil(Math.abs(arg)) * sign;
}

/**
 * transform offsetX/Y from CSSOM coordinate system to proportion of range from 0.
 * with CSSOM, the screen is quadrant Ⅳ (the top-left is (0,0) and the
 * y-coordinates are inverted (on-screen y-coordinates are positive even though
 * they are "below" the x-axis) compared to standard coordinate systems.
 *
 * e.g. transform a MouseEvent.offsetY to proportion of element
 *
 * let height = 400; (element is 400px tall)
 * let offsetY = 389; (near the bottom of the element)
 * transformToCenter(389, 400) = .4725;
 */

function translateToFromOffsetParent(arg: number, range: number): number {
    // round the values up otherwise there may be some deadzones unreachable by panning when movement scale is 1
    return ceilAbs(arg - range / 2);
}

function getRelevantDimensions(
    maxSize: Sizes | number,
    ...[
        naturalWidth,
        naturalHeight,
        offsetWidth,
        offsetHeight,
        containerWidth,
        containerHeight,
    ]: number[]
): [number, number, number] {
    let dimensions: [number, number, number] = [0, 0, 0];
    const naturalAspectRatio = naturalWidth / naturalHeight;
    const containerAspectRatio = containerWidth / containerHeight;
    if (typeof maxSize === 'string' && !Object.keys(CONSTANTS).includes(maxSize)) {
        maxSize = 'MAX_SIZE_CONTAINER_FIT';
    }
    switch (maxSize) {
        case 'MAX_SIZE_CONTAINER_HEIGHT':
            dimensions = [naturalHeight, offsetHeight, containerHeight];
            break;
        case 'MAX_SIZE_CONTAINER_WIDTH':
            dimensions = [naturalWidth, offsetWidth, containerWidth];
            break;
        case 'MAX_SIZE_CONTAINER_FIT':
            dimensions =
                naturalAspectRatio > containerAspectRatio
                    ? [naturalHeight, offsetHeight, containerHeight]
                    : [naturalWidth, offsetWidth, containerWidth];
            break;
        default:
            {
                const maxValue = typeof maxSize === 'number' ? maxSize : CONSTANTS[maxSize];
                dimensions =
                    naturalAspectRatio > containerAspectRatio
                        ? [naturalHeight, offsetHeight, maxValue]
                        : [naturalWidth, offsetWidth, maxValue];
            }
            break;
    }
    return dimensions;
}

function calculateScale(
    {
        naturalWidth,
        naturalHeight,
        offsetWidth,
        offsetHeight,
        offsetParent: { offsetWidth: containerWidth, offsetHeight: containerHeight },
    }: DOMElement<HTMLImageElement>,
    maxSize: Sizes | number,
    maxScale?: number
): number {
    const [naturalDimension, offsetDimension, maxSizeValue] = getRelevantDimensions(
        maxSize,
        naturalWidth,
        naturalHeight,
        offsetWidth,
        offsetHeight,
        containerWidth,
        containerHeight
    );
    const scale = Math.min(naturalDimension, maxSizeValue) / offsetDimension;
    if (maxScale) {
        return Math.min(scale, maxScale);
    }
    return scale;
}

function transform<T extends MouseEvent>(
    {
        offsetX,
        offsetY,
        target: {
            offsetLeft,
            offsetTop,
            offsetHeight,
            offsetWidth,
            offsetParent: { offsetWidth: containerWidth, offsetHeight: containerHeight },
        },
    }: DOMEvent<T>,
    scale: number,
    movementScale: number
): NonNullable<Transform> {
    // translate coordinates from origin (top, left) to origin (center, center) and put in terms of <img/> dimensions
    const cartX = (translateToFromOffsetParent(offsetX, offsetWidth) / offsetWidth) * movementScale;
    const cartY =
        (translateToFromOffsetParent(offsetY, offsetHeight) / offsetHeight) * movementScale;

    // limit coordinates to within <img/> area
    const boundedX = cartClamp(cartX, CONSTANTS.MAX_RANGE);
    const boundedY = cartClamp(cartY, CONSTANTS.MAX_RANGE);

    const maxScaledWidth = offsetWidth * scale;
    const maxScaledHeight = offsetHeight * scale;

    // get difference between event area dimensions and natural img dimensions and divide by scale to put in common terms
    const containerScaleX = (containerWidth - maxScaledWidth) / scale;
    const containerScaleY = (containerHeight - maxScaledHeight) / scale;

    // de-scale diff to put in terms of img dimensions then apply event transform
    const scaledX = containerScaleX * boundedX;
    const scaledY = containerScaleY * boundedY;

    // if scaled image is the same or larger than container, do not allow movement
    const x = maxScaledWidth <= containerWidth ? 0 : scaledX;
    const y = maxScaledHeight <= containerHeight ? 0 : scaledY;

    // use container center as translation of img center as offset for final coordinates
    const centerOffsetX = ((offsetWidth - containerWidth) / 2 + offsetLeft) / scale;
    const centerOffsetY = ((offsetHeight - containerHeight) / 2 + offsetTop) / scale;
    return [x - centerOffsetX, y - centerOffsetY, scale];
}

export type Transform = [x: number, y: number, scale: number] | null;
type ConfigKeys = (keyof typeof defaultConfig)[];
export const configKeys = Object.keys(defaultConfig) as ConfigKeys;

/**
 * defaults
 * @property [maxSize='MAX_SIZE_IMAGE']
 * @property [maxScale=Infinity]
 * @property [movementScale=1.3]
 */

export type Config = {
    maxSize?: Sizes | number;
    maxScale?: number;
    /** "sensitivity" of panning */
    movementScale?: number;
};

/* this is where the magic happens */
export function hoverZoom(
    imgRef: MutableRefObject<HTMLImageElement | null>,
    /** whatever element that wraps the img and limits its visible size by adding overflow: hidden; */
    setTransform: (arg: Transform) => void,
    setCanZoom?: (arg: boolean) => void,
    userConfig: Config = defaultConfig
): {
    handleMouseMove: MouseEventHandler<HTMLDivElement>;
    handleZoom: MouseEventHandler<HTMLDivElement>;
    handleLoad?: () => void;
} {
    const config = { ...defaultConfig, ...userConfig };
    let inZoom: boolean = false;
    let frameId: number | null = null;
    let scale: number | null = null;

    const appliedTransform = (e: MouseEvent, s: number): Transform => {
        if (isDOMEvent(e)) {
            return transform(e, s, config.movementScale);
        }
        return null;
    };

    const appliedScale = (img: DOMElement<HTMLImageElement>): number =>
        calculateScale(img, config.maxSize, config.maxScale);

    function canZoom(): boolean {
        if (isDOMElement(imgRef.current)) {
            scale = appliedScale(imgRef.current);
        }
        return !!scale && scale > 1;
    }

    const handleTransformThrottled: MouseEventHandler = event => {
        if (!inZoom) {
            return;
        }
        frameId = requestAnimationFrame(() => {
            if (!isDOMElement(imgRef.current)) {
                return;
            }
            if (!scale) {
                // initialize if not set
                scale = appliedScale(imgRef.current);
            }
            setTransform(appliedTransform(event.nativeEvent, scale));
        });
    };

    const handleZoom: MouseEventHandler = event => {
        if (!isDOMElement(imgRef.current)) {
            return;
        }
        if (canZoom() && scale) {
            if (!inZoom) {
                inZoom = true;
                setTransform(appliedTransform(event.nativeEvent, scale));
            } else {
                inZoom = false;
                if (frameId) {
                    cancelAnimationFrame(frameId);
                }
                if (imgRef.current) {
                    setTransform(null);
                }
            }
        }
    };
    function handleCanZoom(): void {
        if (setCanZoom) {
            setCanZoom(canZoom());
        }
    }

    const handleScale = (): void => {
        if (!isDOMElement(imgRef.current)) {
            return;
        }
        scale = appliedScale(imgRef.current);
    };

    const handleResize = (): void => {
        handleCanZoom();
        handleScale();
    };

    window.removeEventListener('resize', handleResize);
    if (setCanZoom) {
        window.addEventListener('resize', handleResize, passiveIfSupported);
    }

    return { handleMouseMove: handleTransformThrottled, handleZoom, handleLoad: handleCanZoom };
}
